import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ConditionEditor = ({ condition = {}, devices = [], onSave, onCancel }) => {
  const [type, setType] = useState(condition.type || 'device');

  // Device condition fields
  const [deviceId, setDeviceId] = useState(condition.deviceId || '');
  const [attribute, setAttribute] = useState(condition.attribute || '');
  const [deviceOperator, setDeviceOperator] = useState(condition.operator || '==');
  const [deviceValue, setDeviceValue] = useState(condition.value || '');
  const [parentId, setParentId] = useState(condition.parentId || '');

  // Time condition fields
  const [timeOperator, setTimeOperator] = useState(condition.operator || 'between');
  const [timeStart, setTimeStart] = useState(condition.start || '08:00');
  const [timeEnd, setTimeEnd] = useState(condition.end || '17:00');
  const [timeValue, setTimeValue] = useState(condition.value || '08:00');

  // Day condition fields
  const [dayOperator, setDayOperator] = useState(condition.operator || 'in');
  const [dayValue, setDayValue] = useState(condition.value || []);
  const [selectedDay, setSelectedDay] = useState('');

  // Validation errors
  const [errors, setErrors] = useState({});

  // Stabilize devices reference to avoid unnecessary re-renders from WebSocket updates
  const stableDevices = useMemo(() => {
    return devices;
  }, [devices]);

  // Get device attributes when deviceId changes
  const [deviceAttributes, setDeviceAttributes] = useState([]);
  
  useEffect(() => {
    if (deviceId) {
      // Find the selected device
      const device = stableDevices.find(d => d.id === deviceId);
      
      if (device) {
        // Extract available attributes from device
        const attributes = [];
        
        // Add all properties from controls
        if (device.controls) {
          device.controls.forEach(control => {
            if (control.property && !attributes.includes(control.property)) {
              attributes.push(control.property);
            }
          });
        }
        
        // Add all properties from sensors
        if (device.sensors) {
          device.sensors.forEach(sensor => {
            if (sensor.property && !attributes.includes(sensor.property)) {
              attributes.push(sensor.property);
            }
          });
        }
        
        setDeviceAttributes(attributes);
        
        // If current attribute is not in the list, reset it
        if (attribute && !attributes.includes(attribute)) {
          setAttribute('');
        }
        
        // Check if this is a child device
        if (device.parentId && device.parentId !== deviceId) {
          setParentId(device.parentId);
        } else {
          setParentId('');
        }
      }
    }
  }, [deviceId, stableDevices, attribute]);

  const handleSave = () => {
    const newErrors = {};

    if (type === 'device') {
      if (!deviceId) newErrors.deviceId = 'Please select a device';
      if (!attribute) newErrors.attribute = 'Please select an attribute';
      if (!deviceValue) newErrors.deviceValue = 'Please enter a value';
    } else if (type === 'time') {
      if (timeOperator === 'between') {
        if (!timeStart) newErrors.timeStart = 'Please select a start time';
        if (!timeEnd) newErrors.timeEnd = 'Please select an end time';
      } else {
        if (!timeValue) newErrors.timeValue = 'Please select a time';
      }
    } else if (type === 'day') {
      if (dayOperator === 'in') {
        if (dayValue.length === 0) newErrors.dayValue = 'Please select at least one day';
      } else {
        if (!selectedDay) newErrors.selectedDay = 'Please select a day';
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    let newCondition = { type };

    if (type === 'device') {
      newCondition = {
        ...newCondition,
        deviceId,
        attribute,
        operator: deviceOperator,
        value: deviceValue
      };

      if (parentId) {
        newCondition.parentId = parentId;
      }
    } else if (type === 'time') {
      if (timeOperator === 'between') {
        newCondition = {
          ...newCondition,
          operator: timeOperator,
          start: timeStart,
          end: timeEnd
        };
      } else {
        newCondition = {
          ...newCondition,
          operator: timeOperator,
          value: timeValue
        };
      }
    } else if (type === 'day') {
      newCondition = {
        ...newCondition,
        operator: dayOperator,
        value: dayOperator === 'in' ? [...dayValue] : selectedDay
      };
    }

    onSave(newCondition);
  };

  const handleAddDay = () => {
    if (selectedDay && !dayValue.includes(selectedDay)) {
      setDayValue([...dayValue, selectedDay]);
      setSelectedDay('');
    }
  };

  const handleRemoveDay = (day) => {
    setDayValue(dayValue.filter(d => d !== day));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Label htmlFor="condition-type" className="text-sm font-medium">Condition Type</Label>
        <select
          id="condition-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="device">Device State</option>
          <option value="time">Time</option>
          <option value="day">Day of Week</option>
        </select>
      </div>

      {type === 'device' && (
        <>
          <div className="space-y-1">
            <Label htmlFor="device-id" className="text-sm font-medium">Device</Label>
            <select
              id="device-id"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select a device</option>
              {stableDevices.map(device => (
                <option key={device.id} value={device.id}>
                  {`${device.name} (${device.location || 'N/A'}, Floor ${device.floor || 'N/A'})`}
                </option>
              ))}
            </select>
            {errors.deviceId && (
              <p className="text-sm text-destructive">{errors.deviceId}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="attribute" className="text-sm font-medium">Attribute</Label>
            <select
              id="attribute"
              value={attribute}
              onChange={(e) => setAttribute(e.target.value)}
              required
              disabled={!deviceId}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
            >
              <option value="">Select an attribute</option>
              {deviceAttributes.map(attr => (
                <option key={attr} value={attr}>
                  {attr}
                </option>
              ))}
            </select>
            {errors.attribute && (
              <p className="text-sm text-destructive">{errors.attribute}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="device-operator" className="text-sm font-medium">Operator</Label>
            <select
              id="device-operator"
              value={deviceOperator}
              onChange={(e) => setDeviceOperator(e.target.value)}
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="==">Equals (==)</option>
              <option value="!=">Not Equals (!=)</option>
              <option value=">">Greater Than (&gt;)</option>
              <option value=">=">Greater Than or Equal (&gt;=)</option>
              <option value="<">Less Than (&lt;)</option>
              <option value="<=">Less Than or Equal (&lt;=)</option>
              <option value="contains">Contains</option>
            </select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="device-value" className="text-sm font-medium">Value</Label>
            <Input
              id="device-value"
              type="text"
              value={deviceValue}
              onChange={(e) => setDeviceValue(e.target.value)}
              required
              className="h-10"
            />
            {errors.deviceValue && (
              <p className="text-sm text-destructive">{errors.deviceValue}</p>
            )}
          </div>

          {parentId && (
            <div className="rounded-md border border-border p-3 bg-muted/30">
              <p className="text-xs text-muted-foreground">Parent Device</p>
              <p className="text-sm font-medium mt-1">{devices.find(d => d.id === parentId)?.name || parentId}</p>
            </div>
          )}
        </>
      )}

      {type === 'time' && (
        <>
          <div className="space-y-1">
            <Label htmlFor="time-operator" className="text-sm font-medium">Time Condition</Label>
            <select
              id="time-operator"
              value={timeOperator}
              onChange={(e) => setTimeOperator(e.target.value)}
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="between">Between Times</option>
              <option value="==">At Specific Time</option>
            </select>
          </div>

          {timeOperator === 'between' ? (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="time-start" className="text-xs">Start Time</Label>
                <Input
                  id="time-start"
                  type="time"
                  value={timeStart}
                  onChange={(e) => setTimeStart(e.target.value)}
                  required
                  className="h-10"
                />
                {errors.timeStart && (
                  <p className="text-sm text-destructive">{errors.timeStart}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="time-end" className="text-xs">End Time</Label>
                <Input
                  id="time-end"
                  type="time"
                  value={timeEnd}
                  onChange={(e) => setTimeEnd(e.target.value)}
                  required
                  className="h-10"
                />
                {errors.timeEnd && (
                  <p className="text-sm text-destructive">{errors.timeEnd}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="time-value" className="text-sm font-medium">Time</Label>
              <Input
                id="time-value"
                type="time"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
                required
                className="h-10"
              />
              {errors.timeValue && (
                <p className="text-sm text-destructive">{errors.timeValue}</p>
              )}
            </div>
          )}
        </>
      )}

      {type === 'day' && (
        <>
          <div className="space-y-1">
            <Label htmlFor="day-operator" className="text-sm font-medium">Day Condition</Label>
            <select
              id="day-operator"
              value={dayOperator}
              onChange={(e) => setDayOperator(e.target.value)}
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="in">On Selected Days</option>
              <option value="==">On Specific Day</option>
            </select>
          </div>

          {dayOperator === 'in' ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="day-select" className="text-xs">Add Day</Label>
                  <select
                    id="day-select"
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Select day</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">&nbsp;</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddDay}
                    disabled={!selectedDay}
                    className="w-full h-10"
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {dayValue.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6 border border-dashed rounded-md">No days selected</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {dayValue.map(day => (
                      <div key={day} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 bg-muted/30">
                        <span className="text-xs">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveDay(day)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        >
                          &times;
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {errors.dayValue && (
                  <p className="text-sm text-destructive">{errors.dayValue}</p>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="specific-day" className="text-sm font-medium">Day</Label>
              <select
                id="specific-day"
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select day</option>
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
              {errors.selectedDay && (
                <p className="text-sm text-destructive">{errors.selectedDay}</p>
              )}
            </div>
          )}
        </>
      )}

      <div className="flex gap-2 pt-4 border-t border-muted">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 h-10"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          className="flex-1 h-10"
        >
          Done
        </Button>
      </div>
    </div>
  );
};

export default ConditionEditor;